// TransactionList component displays a list of transactions with filtering and modal details.
//
// Usage:
//   - Admin: <TransactionList /> (shows full date filter bar: today, week, month, all)
//   - POS/Cashier: <TransactionList restrictToToday={true} /> (shows only today's transactions, no date filter bar)
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import axiosInstance from "../../services/axiosInstance";
import EmptyState from "../CommonComponents/EmptyState";
import FilterBar from "../CommonComponents/FilterBar";
import { ListOrdered, Grid, List } from "lucide-react";
import TransactionCard from "./TransactionCard";
import Modal from "../CommonComponents/TransactionModal";
import { fetchBranches } from "../../services/branchApi";
import { fetchUsers } from "../../services/usersApi";
import Pagination from "../CommonComponents/Pagination";

function TransactionList({ onSelect, restrictToToday }) {
    // Get logged-in user info from Redux
    const user = useSelector(state => state.auth.user);
    const customer_id = user?.customer_id;
    const branch_id = user?.branch_id;
    const user_id = user?.id;
    // All useState declarations at the top
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [viewMode, setViewMode] = useState("grid");
    const [dateFilter, setDateFilter] = useState("today");
    const [customStartDate, setCustomStartDate] = useState("");
    const [customEndDate, setCustomEndDate] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [cashierFilter, setCashierFilter] = useState("");
    const [categoryFilter, setCategoryFilter] = useState("");
    const [selectedTx, setSelectedTx] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [branches, setBranches] = useState([]);
    const [users, setUsers] = useState([]);
    const [branchFilter, setBranchFilter] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [categoryOptions, setCategoryOptions] = useState([]);
    const [totals, setTotals] = useState(null);
    const pageSize = 20;

    // Fetch transactions on mount and when filters change
    useEffect(() => {
        if (dateFilter === "custom" && (!customStartDate || !customEndDate)) {
            return;
        }
        setLoading(true);
        console.log('[TransactionList] Filters:', { branchFilter, dateFilter });
        const token = localStorage.getItem("token") || sessionStorage.getItem("token");

        // Build params with filters - Best Practice: Server-side filtering
        let params = { tzOffset: new Date().getTimezoneOffset() };

        if (restrictToToday && customer_id && branch_id) {
            // Cashier view: fixed branch and today only
            params = { customer_id, branch_id, tzOffset: new Date().getTimezoneOffset() };
            if (user_id) params.user_id = user_id;
        } else {
            // Admin view: flexible filtering
            if (customer_id) params.customer_id = customer_id;
            if (branchFilter) params.branch_id = Number(branchFilter);
            if (cashierFilter) params.user_id = Number(cashierFilter);
        }

        // Add status filter (both admin and cashier)
        if (statusFilter) params.status = statusFilter;
        if (categoryFilter) params.category = categoryFilter;

        // Add date filter (admin and cashier)
        if (dateFilter) {
            params.dateFilter = dateFilter; // 'today', 'week', 'month', 'all', 'custom'
            if (dateFilter === "custom") {
                if (customStartDate) params.startDate = customStartDate;
                if (customEndDate) params.endDate = customEndDate;
            }
        }

        const endpoint = user?.user_type === 2 ? "/pos/orders" : "/admin/orders";
        axiosInstance.get(endpoint, {
            params,
            headers: { Authorization: `Bearer ${token}` }
        })
            .then(res => {
                console.log('[TransactionList] API response:', res.data);
                if (Array.isArray(res.data)) {
                    setTransactions(res.data);
                    setTotals(null);
                } else {
                    setTransactions(res.data?.orders || []);
                    setTotals(res.data?.totals || null);
                }
                setError(null);
            })
            .catch((err) => {
                setError(err?.response?.data?.message || "Failed to load transactions");
            })
            .finally(() => setLoading(false));
    }, [restrictToToday, customer_id, branch_id, user_id, branchFilter, cashierFilter, statusFilter, categoryFilter, dateFilter, customStartDate, customEndDate]);

    useEffect(() => {
        if (dateFilter !== "custom") {
            setCustomStartDate("");
            setCustomEndDate("");
        }
    }, [dateFilter]);

    // Fetch branches and users for filters
    useEffect(() => {
        if (!customer_id) return;
        fetchBranches(customer_id)
            .then(res => {
                setBranches(res.data.data);
            })
            .catch(() => {
                setBranches([]);
            });

        fetchUsers()
            .then(res => {
                setUsers(res.data.data || []);
            })
            .catch(() => {
                setUsers([]);
            });
    }, [customer_id]);

    // Helper for relative time
    function getRelativeTime(dateString) {
        const now = new Date();
        const date = new Date(dateString);
        const diff = Math.floor((now - date) / 1000); // seconds
        if (diff < 60) return 'Just Now';
        if (diff < 3600) return `${Math.floor(diff / 60)} mins ago`;
        if (diff < 86400) return `${Math.floor(diff / 3600)} hrs ago`;
        return date.toLocaleDateString();
    }

    const getItemsSubtotal = (tx) => {
        const items = Array.isArray(tx?.products) ? tx.products : [];
        const hasPrice = items.some(item => Number.isFinite(Number(item.price)));
        if (!hasPrice) return Number(tx?.total) || 0;
        return items.reduce((sum, item) => {
            const price = Number(item.price);
            const qty = Number(item.qty || 0);
            if (!Number.isFinite(price) || !Number.isFinite(qty)) return sum;
            return sum + price * qty;
        }, 0);
    };

    const getDisplayTotal = (tx) => {
        if (categoryFilter) return getItemsSubtotal(tx);
        const value = Number(tx?.total);
        return Number.isFinite(value) ? value : 0;
    };

    // Server-side filtering is handled by backend API - no client-side filtering needed
    // This follows best practice for performance and scalability
    const filtered = restrictToToday && user_id
        ? transactions.filter(tx => String(tx.user_id || tx.staff_id || tx.cashier_id) === String(user_id))
        : transactions;

    useEffect(() => {
        setCurrentPage(1);
    }, [restrictToToday, customer_id, branch_id, user_id, branchFilter, cashierFilter, statusFilter, categoryFilter, dateFilter, customStartDate, customEndDate, viewMode]);

    const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
    const safePage = Math.min(currentPage, totalPages);
    const startIndex = (safePage - 1) * pageSize;
    const pagedTransactions = filtered.slice(startIndex, startIndex + pageSize);
    const displayedSalesTotal = typeof totals?.totalSales === "number"
        ? totals.totalSales
        : filtered.reduce((sum, tx) => {
            const value = Number(tx?.total);
            return sum + (Number.isFinite(value) ? value : 0);
        }, 0);
    const displayedTotalCount = typeof totals?.totalCount === "number"
        ? totals.totalCount
        : filtered.length;
    const listPaddingBottom = "pb-8";
    const scrollSpacingBottom = filtered.length > 0 ? "pb-8 mb-12" : "pb-0";
    const availableCategories = categoryOptions;

    useEffect(() => {
        const nextCategories = Array.from(
            new Set(
                (transactions || [])
                    .flatMap(tx => (Array.isArray(tx.products) ? tx.products : []))
                    .map(item => (item?.category || "").trim())
                    .filter(Boolean)
            )
        );
        if (nextCategories.length === 0) return;
        setCategoryOptions(prev => {
            const merged = Array.from(new Set([...(prev || []), ...nextCategories]));
            merged.sort((a, b) => a.localeCompare(b));
            return merged;
        });
    }, [transactions]);

    useEffect(() => {
        if (currentPage > totalPages) {
            setCurrentPage(totalPages);
        }
    }, [currentPage, totalPages]);

    // Prepare filter configurations for FilterBar
    const filterConfigs = [
        {
            type: 'react-select',
            label: 'Branch',
            value: (() => {
                if (!branchFilter) return { value: "", label: "All Branches" };
                const found = (branches || []).find(b => String(b.id) === branchFilter);
                return found ? { value: String(found.id), label: found.name } : { value: branchFilter, label: branchFilter };
            })(),
            onChange: setBranchFilter,
            options: [
                { value: "", label: "All Branches" },
                ...((Array.isArray(branches) ? branches : []).map(branch => ({ value: String(branch.id), label: branch.name })))
            ],
            isSearchable: false,
            placeholder: "All Branches",
            minWidth: '180px',
            hide: restrictToToday, // Hide for cashier view
        },
        {
            type: 'react-select',
            label: 'Date',
            value: (() => {
                const labelMap = {
                    all: "All",
                    today: "Today",
                    week: "This Week",
                    month: "This Month",
                    custom: "Custom",
                };
                const value = dateFilter || "all";
                return { value, label: labelMap[value] || value };
            })(),
            onChange: setDateFilter,
            options: [
                { value: "all", label: "All" },
                { value: "today", label: "Today" },
                { value: "week", label: "This Week" },
                { value: "month", label: "This Month" },
                { value: "custom", label: "Custom" },
            ],
            isSearchable: false,
            placeholder: "All",
            minWidth: '140px',
            hide: false,
        },
        {
            type: 'date',
            label: 'From',
            value: customStartDate,
            onChange: setCustomStartDate,
            minWidth: '150px',
            hide: dateFilter !== "custom",
            max: customEndDate || undefined,
        },
        {
            type: 'date',
            label: 'To',
            value: customEndDate,
            onChange: setCustomEndDate,
            minWidth: '150px',
            hide: dateFilter !== "custom",
            min: customStartDate || undefined,
        },
        {
            type: 'react-select',
            label: 'Cashier',
            value: (() => {
                if (!cashierFilter) return { value: "", label: "All Cashiers" };
                const found = (users || []).find(u => String(u.id) === cashierFilter);
                return found ? { value: String(found.id), label: found.username || found.email } : { value: cashierFilter, label: cashierFilter };
            })(),
            onChange: setCashierFilter,
            options: [
                { value: "", label: "All Cashiers" },
                ...((users || []).map(user => ({
                    value: String(user.id),
                    label: user.username || user.email,
                })))
            ],
            isSearchable: false,
            placeholder: "All Cashiers",
            minWidth: '180px',
            hide: restrictToToday, // Hide for cashier view
        },
        {
            type: 'react-select',
            label: 'Status',
            value: (() => {
                if (!statusFilter) return { value: "", label: "All Status" };
                const labelMap = {
                    PAID: "Paid",
                    Pending: "Pending",
                    Cancelled: "Cancelled",
                };
                return { value: statusFilter, label: labelMap[statusFilter] || statusFilter };
            })(),
            onChange: setStatusFilter,
            options: [
                { value: "", label: "All Status" },
                { value: "PAID", label: "Paid" },
                { value: "Pending", label: "Pending" },
                { value: "Cancelled", label: "Cancelled" },
            ],
            isSearchable: false,
            placeholder: "All Status",
            minWidth: '140px',
            className: 'ml-2',
        },
        {
            type: 'react-select',
            label: 'Category',
            value: (() => {
                if (!categoryFilter) return { value: "", label: "All Categories" };
                return { value: categoryFilter, label: categoryFilter };
            })(),
            onChange: setCategoryFilter,
            options: [
                { value: "", label: "All Categories" },
                ...availableCategories.map(category => ({
                    value: category,
                    label: category,
                }))
            ],
            isSearchable: false,
            placeholder: "All Categories",
            minWidth: '180px',
        },
    ];

    // View toggle buttons
    const viewToggleActions = (
        <>
            <button
                onClick={() => setViewMode("grid")}
                className={`p-2 rounded-xl ${viewMode === "grid" ? "bg-black text-white" : "bg-[#e5e5ea] text-gray-700"} transition-colors`}
                aria-label="Grid view"
            >
                <Grid size={18} />
            </button>
            <button
                onClick={() => setViewMode("list")}
                className={`p-2 rounded-xl ${viewMode === "list" ? "bg-black text-white" : "bg-[#e5e5ea] text-gray-700"} transition-colors`}
                aria-label="List view"
            >
                <List size={18} />
            </button>
        </>
    );

    return (
        <div className="flex flex-col h-full min-h-0">
            {/* Minimalist Apple-inspired Filter Bar - Always visible */}
            <FilterBar filters={filterConfigs} extraActions={viewToggleActions} />

            {/* Loading State */}
            {loading && (
                <div className="flex-1 flex items-center justify-center">
                    <div>Loading transactions...</div>
                </div>
            )}

            {/* Error State */}
            {error && !loading && (
                <div className="flex-1 flex items-center justify-center">
                    <EmptyState
                        icon={ListOrdered}
                        title="Failed to Load Transactions"
                        message="Check your internet connection and try again."
                        onRetry={() => window.location.reload()}
                        retryLabel="Reload"
                    />
                </div>
            )}

            {/* Empty State */}
            {!loading && !error && filtered.length === 0 && (
                <div className="flex-1 flex items-center justify-center">
                    <EmptyState
                        icon={ListOrdered}
                        title="No Transactions"
                        message="No transaction data available for the selected period."
                    />
                </div>
            )}

            {/* Transaction List */}
            {!loading && !error && filtered.length > 0 && (
                <div className={`flex-1 min-h-0 overflow-y-auto mt-2 ${scrollSpacingBottom}`}>
                    <div className={viewMode === "grid"
                        ? `grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 ${listPaddingBottom}`
                        : `flex flex-col gap-2 ${listPaddingBottom}`}>
                        {pagedTransactions.map(tx => (
                            <TransactionCard
                                key={tx.id}
                                tx={tx}
                                displayTotal={getDisplayTotal(tx)}
                                totalLabel={categoryFilter ? "Items Subtotal" : null}
                                getRelativeTime={getRelativeTime}
                                onClick={() => {
                                    setSelectedTx(tx);
                                    setModalOpen(true);
                                    if (onSelect) onSelect(tx.id);
                                }}
                            />
                        ))}
                    </div>
                </div>
            )}

            {!loading && !error && filtered.length > 0 && (
                <div className="fixed bottom-0 left-0 right-0 lg:left-64 px-4 py-5 z-20">
                    <div className="mx-auto max-w-7xl">
                        <Pagination
                            currentPage={safePage}
                            totalPages={totalPages}
                            onPageChange={setCurrentPage}
                            totalCount={displayedTotalCount}
                            displayedSalesTotal={displayedSalesTotal}
                        />
                    </div>
                </div>
            )}

            <Modal open={modalOpen} onClose={() => setModalOpen(false)}>
                {selectedTx ? (
                    <div>
                        <h2 className="font-bold text-lg mb-2">Order #{selectedTx.order_number}</h2>
                        <div className="mb-1 text-sm text-gray-600">Date: {new Date(selectedTx.created_at).toLocaleDateString()} {new Date(selectedTx.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
                        <div className="mb-1 text-sm">Status: <span className="font-semibold">{selectedTx.status}</span></div>
                        <div className="mb-1 text-sm">Payment: <span className="font-semibold">{selectedTx.payment_method}</span></div>
                        <div className="mb-1 text-sm">
                            Cashier: <span className="font-semibold">{selectedTx.cashier_name || selectedTx.cashier || selectedTx.user_name || '-'}</span>
                        </div>
                        {categoryFilter && (
                            <div className="mb-2">
                                <span className="text-[11px] font-semibold text-gray-600">Category:</span>{" "}
                                <span className="inline-flex items-center rounded-full bg-amber-100 text-amber-800 px-2 py-0.5 text-[11px] font-semibold">
                                    {categoryFilter}
                                </span>
                            </div>
                        )}
                        <div className="mb-1 text-sm">Items: <span className="font-semibold">{selectedTx.products ? selectedTx.products.reduce((sum, item) => sum + (item.qty || 0), 0) : 0}</span></div>
                        {selectedTx.products && selectedTx.products.length > 0 && (
                            <div className="mb-2">
                                <div className="grid grid-cols-12 text-[11px] text-gray-500 border-b pb-1">
                                    <div className={categoryFilter ? "col-span-8" : "col-span-5"}>Item</div>
                                    {!categoryFilter && <div className="col-span-3 text-center">Category</div>}
                                    <div className="col-span-2 text-right">Price</div>
                                    <div className="col-span-2 text-right">Qty</div>
                                </div>
                                <div className="max-h-40 overflow-y-auto">
                                    {selectedTx.products.map((item, idx) => (
                                        <div key={idx} className="grid grid-cols-12 text-xs text-gray-700 py-1 border-b border-gray-100">
                                            <div className={categoryFilter ? "col-span-8 pr-2 truncate" : "col-span-5 pr-2 truncate"}>{item.name || "Item"}</div>
                                            {!categoryFilter && <div className="col-span-3 text-center truncate">{item.category || "-"}</div>}
                                            <div className="col-span-2 text-right text-gray-500">{"\u20b1"}{Number(item.price || 0).toLocaleString()}</div>
                                            <div className="col-span-2 text-right text-gray-500">x{item.qty || 1}</div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                        <div className="mt-2 text-right">
                            <span className="text-xs text-gray-500 block">{categoryFilter ? "Items Subtotal:" : "Total Amount Paid:"}</span>
                            <span className="text-lg font-bold text-grey">{"\u20b1"}{getDisplayTotal(selectedTx).toLocaleString()}</span>
                        </div>
                    </div>
                ) : null}
            </Modal>
        </div>
    );
}

export default TransactionList;
